import React from 'react';
import type { WithTranslation } from 'react-i18next';
import { withTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import { Spinner } from '@/components/atoms/Spinner';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import { describeApiError } from '@/domain/shared/describeApiError';

import styles from './AddBioscope.module.scss';

interface ClientOption {
    id: number;
    name: string;
}

interface FieldError {
    key: string;
    message: string;
}

interface AddBioscopeProps extends WithTranslation {
    isOpen: boolean;
    clients: ClientOption[];
    createBioscope: (clientId: string, bioscopeId: string) => Promise<unknown>;
    onClose: () => void;
}

interface AddBioscopeState {
    clientId: string;
    bioscopeId: string;
    errors: FieldError[];
    saving: boolean;
    error?: React.ReactNode;
}

function isFieldError(value: unknown): value is FieldError {
    return (
        typeof value === 'object' &&
        value !== null &&
        'key' in value &&
        typeof value.key === 'string' &&
        'message' in value &&
        typeof value.message === 'string'
    );
}

/**
 * Whether the create endpoint refused an ID that is taken. It answers 400 with the field name
 * as `message` and the reason in `error`.
 */
/**
 * The create endpoint answers a taken id with this reason in `message`. Backends deployed before
 * that named the field in `message` instead, so both are read until they are gone.
 */
const TAKEN_ID_MESSAGES: readonly string[] = ['Bioscope id is already used', 'bioscopeId'];

function isTakenIdError(error: unknown): boolean {
    const described = describeApiError(error);
    return (
        described.kind === 'client' &&
        described.message !== undefined &&
        TAKEN_ID_MESSAGES.includes(described.message)
    );
}

/** The per-field messages of a failed save, when the error carries them. */
function fieldErrorsOf(error: unknown): FieldError[] | null {
    if (typeof error !== 'object' || error === null || !('error' in error)) return null;
    const inner = error.error;
    if (typeof inner !== 'object' || inner === null || !('messages' in inner)) return null;
    const { messages } = inner;
    return Array.isArray(messages) && messages.every(isFieldError) ? messages : null;
}

class AddBioscope extends React.Component<AddBioscopeProps, AddBioscopeState> {
    constructor(props: AddBioscopeProps) {
        super(props);
        this.state = {
            clientId: '',
            bioscopeId: '',
            errors: [],
            saving: false,
        };
    }

    getError(key: string): string | false {
        const error = this.state.errors.find((e) => e.key === key);

        return error ? error.message : false;
    }

    onSubmit(): void {
        this.setState({ saving: true, error: undefined, errors: [] });
        this.props
            .createBioscope(this.state.clientId, this.state.bioscopeId)
            .then(() => {
                this.setState({ saving: false });
                this.props.onClose();
            })
            .catch((error: unknown) => {
                const fieldErrors = isTakenIdError(error)
                    ? [{ key: 'bioscopeId', message: this.props.t('internal.addBioscope.idTaken') }]
                    : fieldErrorsOf(error);
                if (fieldErrors) {
                    this.setState({
                        errors: fieldErrors,
                        saving: false,
                    });
                } else {
                    this.setState({
                        error: apiErrorContent(
                            error,
                            this.props.t('internal.addBioscope.saveFailed')
                        ),
                        saving: false,
                    });
                }
            });
    }

    buildContent(): React.ReactNode {
        const bioscopeIdError = this.getError('bioscopeId');

        return (
            <React.Fragment>
                <FormField
                    label={this.props.t('common.labels.id')}
                    error={bioscopeIdError || undefined}>
                    <TextField
                        type="number"
                        name="id"
                        placeholder={this.props.t('internal.bioscopes.autoGenerateId')}
                        value={this.state.bioscopeId}
                        onChange={(e) => {
                            this.setState({
                                bioscopeId: e.target.value,
                            });
                        }}
                        invalid={!!bioscopeIdError}
                    />
                </FormField>
                <FormField label={this.props.t('common.labels.client')}>
                    <Select
                        name="clientId"
                        value={this.state.clientId}
                        onChange={(e) => {
                            this.setState({
                                clientId: e.target.value,
                            });
                        }}>
                        <option value="">{this.props.t('common.labels.none')}</option>
                        {this.props.clients.map((client) => (
                            <option key={client.id} value={client.id}>
                                {client.name}
                            </option>
                        ))}
                    </Select>
                </FormField>
                {this.state.error ? <Alert tone="danger">{this.state.error}</Alert> : ''}
            </React.Fragment>
        );
    }

    render(): React.ReactNode {
        return (
            <ModalComponent
                isOpen={this.props.isOpen}
                onClose={this.props.onClose}
                centered={false}
                title={this.props.t('internal.bioscopes.addBioscopeTitle')}>
                <ModalBody>{this.buildContent()}</ModalBody>
                <ModalFooter>
                    {this.state.saving ? (
                        <div className={styles.saving}>
                            <Spinner size="md" />
                        </div>
                    ) : null}
                    <Button variant="secondary" onClick={this.props.onClose}>
                        {this.props.t('common.actions.cancel')}
                    </Button>
                    <Button variant="primary" onClick={() => this.onSubmit()}>
                        {this.props.t('common.actions.save')}
                    </Button>
                </ModalFooter>
            </ModalComponent>
        );
    }
}

export default withTranslation()(AddBioscope);
